// SPDX-License-Identifier: AGPL-3.0-or-later

import FormField from '@app/features/auth/flow/AuthFormField';
import styles from '@app/features/auth/flow/auth_login_core/AuthLoginEmailPasswordForm.module.css';
import {
	EMAIL_DESCRIPTOR,
	PASSWORD_DESCRIPTOR,
	USERNAME_DESCRIPTOR,
} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {Button} from '@app/features/ui/button/Button';
import {flxElementClassName} from '@app/lib/react';
import {useLingui} from '@lingui/react/macro';
import type React from 'react';
import {useId} from 'react';

type FieldErrors = ReadonlyMap<string, string> | null | undefined;

export interface AuthFormControllerLike {
	handleSubmit: (event: React.SubmitEvent<HTMLFormElement>) => void;
	getValue: (name: string) => string;
	setValue: (name: string, value: string) => void;
	getError: (name: string) => string | null | undefined;
	isSubmitting?: boolean;
}

interface AuthEmailPasswordFormClasses {
	form: string;
}

interface Props {
	form: AuthFormControllerLike;
	isLoading: boolean;
	fieldErrors?: FieldErrors;
	submitLabel: React.ReactNode;
	classes: AuthEmailPasswordFormClasses;
	links?: React.ReactNode;
	linksWrapperClassName?: string;
	disableSubmit?: boolean;
	footerStart?: React.ReactNode;
	statusMessage?: string | null;
	identifierField?: 'email' | 'login';
}

export default function AuthLoginEmailPasswordForm({
	form,
	isLoading,
	fieldErrors,
	submitLabel,
	classes,
	links,
	linksWrapperClassName,
	disableSubmit,
	footerStart,
	statusMessage,
	identifierField = 'email',
}: Props) {
	const {i18n} = useLingui();
	const emailId = useId();
	const passwordId = useId();
	const isPending = isLoading || Boolean(form.isSubmitting);
	const hasStatus = statusMessage != null && statusMessage.length > 0;
	return (
		<form
			className={classes.form}
			onSubmit={form.handleSubmit}
			autoComplete="on"
			name="login"
			data-flx="auth.flow.auth-login-core.auth-login-email-password-form.form.submit"
		>
			<FormField
				id={emailId}
				name={identifierField}
				type={identifierField === 'email' ? 'email' : 'text'}
				autoComplete="username"
				autoCapitalize="none"
				autoCorrect="off"
				enterKeyHint="next"
				spellCheck={false}
				data-step-focus="true"
				required
				label={i18n._(identifierField === 'email' ? EMAIL_DESCRIPTOR : USERNAME_DESCRIPTOR)}
				value={form.getValue(identifierField)}
				onChange={(value) => form.setValue(identifierField, value)}
				error={form.getError(identifierField) || fieldErrors?.get(identifierField)}
				data-flx="auth.flow.auth-login-core.auth-login-email-password-form.form-field.set-value.email"
			/>
			<FormField
				id={passwordId}
				name="password"
				type="password"
				autoComplete="current-password"
				enterKeyHint="done"
				required
				label={i18n._(PASSWORD_DESCRIPTOR)}
				value={form.getValue('password')}
				onChange={(value) => form.setValue('password', value)}
				error={form.getError('password') || fieldErrors?.get('password')}
				data-flx="auth.flow.auth-login-core.auth-login-email-password-form.form-field.set-value.password"
			/>
			{links ? (
				<div className={linksWrapperClassName} data-flx="auth.flow.auth-login-core.auth-login-email-password-form.div">
					{links}
				</div>
			) : null}
			<flx-auth-login-email-password-form-footer
				className={flxElementClassName(styles.footer)}
				data-flx="auth.flow.auth-login-core.auth-login-email-password-form.footer"
			>
				{hasStatus ? (
					<span
						className={styles.status}
						role="status"
						data-flx="auth.flow.auth-login-core.auth-login-email-password-form.status"
					>
						{statusMessage}
					</span>
				) : (
					footerStart
				)}
				<Button
					type="submit"
					submitting={isPending}
					disabled={Boolean(disableSubmit)}
					data-flx="auth.flow.auth-login-core.auth-login-email-password-form.button.submit"
				>
					{submitLabel}
				</Button>
			</flx-auth-login-email-password-form-footer>
		</form>
	);
}
